/**
 * As contas da LINHA DO TEMPO da lista de Itens
 * (SPEC-20261002-1241-linha-do-tempo-de-itens, protótipo "Itens de Trabalho -
 * Prototipo.html"): escala por semana, faixa de datas, posição e largura das
 * barras, e o estado de cada barra. Fora do componente para dar para testar
 * sem navegador — é onde um dia a mais ou a menos passaria despercebido.
 */

/** Largura de uma semana na grade, como no protótipo. */
export const LARGURA_DA_SEMANA = 108
const DIA_MS = 86_400_000
const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']

/** Meia-noite LOCAL do dia (as datas do item são dias, não instantes). */
export function diaDe(valor: string | Date): Date {
  const d = typeof valor === 'string' ? new Date(valor) : valor
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

/** Segunda-feira da semana do dia. */
export function inicioDaSemana(dia: Date): Date {
  const d = diaDe(dia)
  const deslocamento = (d.getDay() + 6) % 7 // segunda = 0
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - deslocamento)
}

/** Dias inteiros de `de` até `ate` (ignora horário de verão). */
export function diasEntreDatas(de: Date, ate: Date): number {
  return Math.round((diaDe(ate).getTime() - diaDe(de).getTime()) / DIA_MS)
}

/** Pixels a partir do início da faixa, para um número de dias. */
export const pixels = (dias: number) => Math.round((dias * LARGURA_DA_SEMANA) / 7)

/** Número da semana ISO (o "semana 41" do cabeçalho). */
export function semanaIso(dia: Date): number {
  const d = new Date(Date.UTC(dia.getFullYear(), dia.getMonth(), dia.getDate()))
  const dow = d.getUTCDay() || 7
  d.setUTCDate(d.getUTCDate() + 4 - dow)
  const inicioDoAno = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
  return Math.ceil(((d.getTime() - inicioDoAno.getTime()) / DIA_MS + 1) / 7)
}

/**
 * A barra de um item: do início ao fim. Sem início, nasce no prazo; sem
 * prazo, no início — um dia só. Sem nenhuma das duas, não há barra.
 */
export function periodoDoItem(item: { startAt?: string | null; dueAt?: string | null }): { de: Date; ate: Date } | null {
  const inicio = item.startAt ? diaDe(item.startAt) : null
  const fim = item.dueAt ? diaDe(item.dueAt) : null
  if (!inicio && !fim) return null
  const de = inicio ?? fim!
  const ate = fim ?? inicio!
  return ate < de ? { de: ate, ate: de } : { de, ate }
}

/**
 * Faixa da grade: da semana do período mais cedo até a do mais tarde, sempre
 * com a semana de hoje dentro e uma semana de folga em cada ponta. Limitada a
 * 2 anos para trás e 2 para a frente de hoje — uma data errada (1900, 2999)
 * não pode virar uma grade de séculos.
 */
export function faixaDaGrade(periodos: ({ de: Date; ate: Date } | null)[], hoje: Date): { inicio: Date; semanas: number } {
  const dias = periodos.filter((p): p is { de: Date; ate: Date } => p !== null)
  const limiteAntes = new Date(hoje.getFullYear() - 2, hoje.getMonth(), hoje.getDate())
  const limiteDepois = new Date(hoje.getFullYear() + 2, hoje.getMonth(), hoje.getDate())
  let menor = diaDe(hoje)
  let maior = diaDe(hoje)
  for (const p of dias) {
    if (p.de < menor) menor = p.de < limiteAntes ? limiteAntes : p.de
    if (p.ate > maior) maior = p.ate > limiteDepois ? limiteDepois : p.ate
  }
  const inicio = inicioDaSemana(new Date(menor.getFullYear(), menor.getMonth(), menor.getDate() - 7))
  const fimDaSemana = inicioDaSemana(new Date(maior.getFullYear(), maior.getMonth(), maior.getDate() + 7))
  const semanas = Math.max(4, Math.round(diasEntreDatas(inicio, fimDaSemana) / 7) + 1)
  return { inicio, semanas }
}

/** As colunas do cabeçalho: dia e mês da segunda-feira, e a semana ISO. */
export function semanasDaGrade(faixa: { inicio: Date; semanas: number }, hoje: Date) {
  const semanaDeHoje = inicioDaSemana(hoje).getTime()
  return Array.from({ length: faixa.semanas }, (_, i) => {
    const d = new Date(faixa.inicio.getFullYear(), faixa.inicio.getMonth(), faixa.inicio.getDate() + i * 7)
    return {
      chave: d.toISOString(),
      dia: d.getDate(),
      mes: MESES[d.getMonth()]!,
      semana: semanaIso(d),
      atual: d.getTime() === semanaDeHoje,
    }
  })
}

/** Posição e largura da barra na grade (o último dia conta inteiro). */
export function geometriaDaBarra(periodo: { de: Date; ate: Date }, inicioDaFaixa: Date) {
  const esquerda = pixels(diasEntreDatas(inicioDaFaixa, periodo.de))
  const direita = pixels(diasEntreDatas(inicioDaFaixa, periodo.ate) + 1)
  return { esquerda, largura: Math.max(direita - esquerda, 6) }
}

export type EstadoDaBarra = 'feito' | 'andamento' | 'aFazer'

/** Concluído (terminal), a fazer (status inicial) ou em andamento (o resto). */
export function estadoDaBarra(status: { isTerminal: boolean }, inicial: boolean): EstadoDaBarra {
  if (status.isTerminal) return 'feito'
  return inicial ? 'aFazer' : 'andamento'
}

/**
 * Atraso: prazo passado e não concluído. A barra listrada vai do dia seguinte
 * ao prazo até hoje.
 */
export function atrasoDoItem(
  item: { dueAt?: string | null; status: { isTerminal: boolean } },
  hoje: Date,
  inicioDaFaixa: Date,
): { esquerda: number; largura: number } | null {
  if (!item.dueAt || item.status.isTerminal) return null
  const prazo = diaDe(item.dueAt)
  if (diaDe(hoje) <= prazo) return null
  const esquerda = pixels(diasEntreDatas(inicioDaFaixa, prazo) + 1)
  const direita = pixels(diasEntreDatas(inicioDaFaixa, hoje) + 1)
  return { esquerda, largura: Math.max(direita - esquerda, 4) }
}
